import { Helmet } from 'react-helmet-async';
import keywords from '../data/keywords.json';

interface SEOProps {
    title: string;
    description: string;
    path: string;
    city?: string;
    service?: string;
}

export const SEO: React.FC<SEOProps> = ({ 
  title,
  description,
  path,
  city,
  service
}) => {
  const baseUrl = 'https://sellcareasy.com.au';
  const currentUrl = `${baseUrl}${path}`;
  
  // For home page, use just the title without the site name
  const isHomePage = path === '/';
  const pageTitle = isHomePage ? title : `${title} | Sell Car Easy`;
  
  const pageDescription = description || 
    `Professional ${service?.toLowerCase()} service in ${city}. We provide accurate and reliable car valuations.`;

  // Generate localized keywords based on service and city
  const localizedKeywords = city ? keywords.map(keyword => 
    `${keyword} ${city}`
  ) : keywords;

  // Generate structured data for service pages
  const generateStructuredData = () => {
    if (!service || !city) return null;

    return {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": `${service} in ${city}`,
      "description": pageDescription,
      "areaServed": {
        "@type": "City",
        "name": city
      },
      "provider": {
        "@type": "Organization",
        "name": "Sell Car Easy",
        "url": baseUrl
      },
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "AUD"
      }
    };
  };
  
  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={localizedKeywords.join(', ')} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={`${baseUrl}/img/og-image.jpg`} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={currentUrl} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={`${baseUrl}/img/og-image.jpg`} />

      {/* Additional SEO tags */}
      <meta name="robots" content="index, follow" />
      <meta name="language" content="English" />
      <meta name="author" content="Sell Car Easy" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={currentUrl} />

      {/* Structured Data */}
      {generateStructuredData() && (
        <script type="application/ld+json">
          {JSON.stringify(generateStructuredData())}
        </script>
      )}
    </Helmet>
  );
};